import React, { useState, useEffect, useCallback } from "react";
import TablePageLayout from "../../../components/TablePageLayout";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../shadcn/ui/table";
import { Badge } from "../../../shadcn/ui/badge";
import { Button } from "../../../shadcn/ui/button";
import { FaEllipsisH } from "react-icons/fa";
import { organizationApiService } from "../../../services/apiV2Service";
import { showApiMessage } from "../../../utils/messageHandler";
import logger from "../../../utils/logger";
import EditOrganizationModal from "../../host-experience/host-clients/components/modals/EditOrganizationModal";
import { useHostOrganizationEdit } from "../hooks/useHostOrganizationEdit";

const DisabledHosts = () => {
  const [disabledHosts, setDisabledHosts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPageSize, setTotalPageSize] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(0);
  const [searchValue, setSearchValue] = useState("");
  const [loading, setLoading] = useState(true);

  // Edit functionality using shared hook
  const {
    showEditModal,
    setShowEditModal,
    selectedOrganization,
    editFormData,
    editLoading,
    editError,
    fetchingData,
    commissionPercentage,
    setCommissionPercentage,
    isCustomRevenueShare,
    defaultCommission,
    handleEditShow,
    handleEditClose,
    handleInputChange,
    handleSubmit,
    handleDeleteRevenueShare,
  } = useHostOrganizationEdit();

  useEffect(() => {
    loadOrganizationsByStatus();
  }, []);

  // Auto-search after 3 characters
  useEffect(() => {
    if (searchValue.length >= 3 || searchValue.length === 0) {
      setCurrentPage(1);
      loadOrganizationsByStatus(1, searchValue);
    }
  }, [searchValue]);

  const loadOrganizationsByStatus = async (page = currentPage, search = searchValue) => {
    setLoading(true);
    try {
      // Get DISABLED organizations for both HOST_PROPERTY_DISTRIBUTION and HOST_PROPERTY_MANAGEMENT types
      const distributionParams = {
        typeId: 2, // HOST_PROPERTY_DISTRIBUTION
        status: "DISABLED",
        page: page,
        limit: 10,
        ...(search && { q: search }), // Add q param if search exists
      };

      const managementParams = {
        typeId: 3, // HOST_PROPERTY_MANAGEMENT
        status: "DISABLED",
        page: page,
        limit: 10,
        ...(search && { q: search }), // Add q param if search exists
      };

      const [distributionResponse, managementResponse] = await Promise.all([
        organizationApiService.getOrganizations(distributionParams),
        organizationApiService.getOrganizations(managementParams),
      ]);

      const distributionOrgs = distributionResponse.data?.data || [];
      const managementOrgs = managementResponse.data?.data || [];
      const allOrganizations = [...distributionOrgs, ...managementOrgs];

      const pagination = distributionResponse.data?.pagination || {};

      // Add checked property to each organization
      const organizationsWithChecked = allOrganizations.map(org => ({
        ...org,
        checked: false,
      }));

      setTotalPageSize(pagination.totalCount || allOrganizations.length);
      setItemsPerPage(pagination.limit || 10);
      setDisabledHosts(organizationsWithChecked);
    } catch (err) {
      logger.error("Error loading disabled organizations:", err);
      showApiMessage(err, "fetch disabled organizations");
    } finally {
      setLoading(false);
    }
  };

  const changePage = async selectedObj => {
    const pageNumber = Number(selectedObj.selected) || 1;
    setCurrentPage(pageNumber);
    // Reload data for the new page
    loadOrganizationsByStatus();
  };

  const performSearch = async () => {
    // Reset to first page when searching
    setCurrentPage(1);
    setLoading(true);

    try {
      // For now, we'll use the same loadOrganizationsByStatus function
      // Later this can be enhanced to include search functionality
      await loadOrganizationsByStatus();
    } catch (err) {
      logger.error("Error searching hosts:", err);
      showApiMessage(err, "search hosts");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (hostId) => {
    try {
      await organizationApiService.updateApprovedStatus(hostId, { approved: true });
      showApiMessage({ message: "Organization approved successfully" }, "success");
      loadOrganizationsByStatus();
    } catch (err) {
      logger.error("Error approving organization:", err);
      showApiMessage(err, "approve organization");
    }
  };

  return (
    <TablePageLayout
      title="Disabled Hosts"
      subtitle="Manage and view disabled hosts"
      breadcrumbs={[{ label: "Home", path: "/host" }, { label: "Disabled Hosts" }]}
      showSearch={true}
      searchValue={searchValue}
      onSearchChange={e => setSearchValue(e.target.value)}
      onSearchSubmit={performSearch}
      searchPlaceholder="Search hosts..."
      loading={loading}
      emptyMessage="No disabled hosts found"
      showPagination={Array.isArray(disabledHosts) && disabledHosts.length > 0 && totalPageSize > 0}
      currentPage={currentPage}
      totalCount={totalPageSize}
      pageSize={itemsPerPage}
      onPageChange={changePage}
    >
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-12 font-semibold">#</TableHead>
              <TableHead className="font-semibold">Company</TableHead>
              <TableHead className="font-semibold">Company Rep.</TableHead>
              <TableHead className="font-semibold">Status</TableHead>
              <TableHead className="font-semibold">Date Disabled</TableHead>
              <TableHead className="font-semibold text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {disabledHosts.map((host, index) => (
              <TableRow key={host.id} className="hover:bg-muted/50">
                <TableCell className="text-gray-500 font-medium">
                  {(currentPage - 1) * itemsPerPage + index + 1}
                </TableCell>
                <TableCell>
                  <div className="flex flex-col">
                    <span className="font-medium">{host.name}</span>
                    <span className="text-sm text-gray-600">{host.address || "N/A"}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex flex-col">
                    <span>{host.contactEmail || "N/A"}</span>
                    <span className="text-sm text-gray-600">{host.contactMsisdn || "N/A"}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex flex-col gap-1 items-start">
                    <Badge variant="secondary" className="bg-red-100 text-red-800 hover:bg-red-100">
                      {host.status || "DISABLED"}
                    </Badge>
                    <Badge
                      variant="outline"
                      className={
                        host.approved
                          ? "bg-green-50 text-green-700 border-green-200"
                          : "bg-gray-50 text-gray-600 border-gray-200"
                      }
                    >
                      {host.approved ? "Approved" : "Pending Approval"}
                    </Badge>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="text-sm">
                    <div className="font-medium">{new Date(host.createdAt).toLocaleDateString()}</div>
                    <div className="text-xs text-muted-foreground">{new Date(host.createdAt).toLocaleTimeString()}</div>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex space-x-2 items-center justify-end">
                    <Button
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={() => handleApprove(host.id)}
                    >
                      Approve
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleEditShow(host)} title="Edit Host">
                      <FaEllipsisH className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Edit Host Dialog */}
      <EditOrganizationModal
        isOpen={showEditModal}
        onClose={handleEditClose}
        selectedOrganization={selectedOrganization}
        editOrganizationForm={editFormData}
        editOrganizationLoading={editLoading}
        editOrganizationError={editError}
        fetchingOrganizationData={fetchingData}
        onInputChange={handleInputChange}
        onSubmit={async () => {
          const result = await handleSubmit();
          if (result?.success) {
            loadOrganizationsByStatus();
          }
        }}
        isHostMode={true}
        commissionPercentage={commissionPercentage}
        onCommissionChange={setCommissionPercentage}
        defaultCommission={defaultCommission}
        isCustomRevenueShare={isCustomRevenueShare}
        onDeleteRevenueShare={handleDeleteRevenueShare}
      />
    </TablePageLayout>
  );
};

export default DisabledHosts;
